import { map, mergeMap, catchError, tap, filter } from 'rxjs/operators'
import { of } from 'rxjs'
import { ofType, combineEpics } from 'redux-observable'

import { logger } from '../../util/log.helpers'
import { FILE_UPLOAD_ACTIONS, QUEST_ACTION } from '../../constants/actions'
import apiService from '../../services/api'
import { wrapUserAccessToken } from '../../util/auth.helpers'
import { commonParser } from '../../util/apiParser.helpers'
import {
  compareFileUploadType,
  extractBrandId,
  extractFileUploadType
} from '../../util/epics.helpers'
import {
  questDataObjFormat,
  isMediaAvailableForQuest,
  questCreateFieldsFormat,
  questUpdateFieldsFormat,
  extractQuestListProfile,
  extractQuestProfile
} from '../../util/quest.helpers'
import { FILE_UPLOAD_TYPE } from '../../constants/input'
import { API_PATHS } from '../../constants/api'

const handleCreateQuestIfNoMediaChange = (action$, state$) =>
  action$.pipe(
    ofType(QUEST_ACTION.ON_CREATE_INIT_QUEST),
    filter(() => !isMediaAvailableForQuest(state$)),
    map(() => ({
      type: QUEST_ACTION.ON_CREATE_QUEST
    }))
  )

const handleCreateQuestIfMediaChange = (action$, state$) =>
  action$.pipe(
    ofType(QUEST_ACTION.ON_CREATE_INIT_QUEST),
    filter(() => isMediaAvailableForQuest(state$)),
    map(({ payload: { file } }) => ({
      type: FILE_UPLOAD_ACTIONS.ON_GET_PRESIGNED_URL,
      payload: { file, action: FILE_UPLOAD_TYPE.CREATE_QUEST }
    }))
  )

const handleCreateQuestWithUploadMediaEpic = (action$, state$) =>
  action$.pipe(
    ofType(FILE_UPLOAD_ACTIONS.ON_UPLOAD_FILE_SUCCESS),
    filter(() =>
      compareFileUploadType(
        extractFileUploadType(state$),
        FILE_UPLOAD_TYPE.CREATE_QUEST
      )
    ),
    map(() => ({
      type: QUEST_ACTION.ON_CREATE_QUEST
    }))
  )

const handleCreateQuestEpic = (action$, state$) =>
  action$.pipe(
    ofType(QUEST_ACTION.ON_CREATE_QUEST),
    mergeMap(() =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .createBrandResource(
            API_PATHS.QUESTS,
            extractBrandId(state$),
            questCreateFieldsFormat(state$),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => {
              return {
                type: QUEST_ACTION.ON_CREATE_QUEST_SUCCESS,
                payload: commonParser(response.data)
              }
            }),
            catchError((error) =>
              of({
                type: QUEST_ACTION.ON_CREATE_QUEST_FAILED,
                payload: error
              })
            )
          )
      )
    )
  )

const handleToggleQuestEpic = (action$) =>
  action$.pipe(
    ofType(QUEST_ACTION.ON_TOGGLE_QUEST),
    map(({ payload }) => ({
      type: QUEST_ACTION.ON_UPDATE_QUEST,
      payload
    }))
  )

const handleUpdateQuestIfNoMediaChange = (action$, state$) =>
  action$.pipe(
    ofType(QUEST_ACTION.ON_UPDATE_INIT_QUEST),
    filter(() => !isMediaAvailableForQuest(state$)),
    map(({ payload: { quest } }) => ({
      type: QUEST_ACTION.ON_UPDATE_QUEST,
      payload: { quest }
    }))
  )

const handleUpdateQuestIfMediaChange = (action$, state$) =>
  action$.pipe(
    ofType(QUEST_ACTION.ON_UPDATE_INIT_QUEST),
    filter(() => isMediaAvailableForQuest(state$)),
    map(({ payload: { file } }) => ({
      type: FILE_UPLOAD_ACTIONS.ON_GET_PRESIGNED_URL,
      payload: { file, action: FILE_UPLOAD_TYPE.UPDATE_QUEST }
    }))
  )

const handleUpdateQuestWithUploadMediaEpic = (action$, state$) =>
  action$.pipe(
    ofType(FILE_UPLOAD_ACTIONS.ON_UPLOAD_FILE_SUCCESS),
    filter(() =>
      compareFileUploadType(
        extractFileUploadType(state$),
        FILE_UPLOAD_TYPE.UPDATE_QUEST
      )
    ),
    map(() => ({
      type: QUEST_ACTION.ON_UPDATE_QUEST,
      payload: {
        quest: questUpdateFieldsFormat(
          extractQuestListProfile(state$).selected?.id,
          extractQuestProfile(state$)
        )
      }
    }))
  )

const handleUpdateQuestEpic = (action$, state$) =>
  action$.pipe(
    ofType(QUEST_ACTION.ON_UPDATE_QUEST),
    mergeMap(
      ({
        payload: {
          quest: { id, data }
        }
      }) =>
        wrapUserAccessToken((accessToken) =>
          apiService
            .updateBrandResource(
              API_PATHS.QUESTS,
              extractBrandId(state$),
              id,
              questDataObjFormat(data),
              accessToken
            )
            .pipe(
              tap((data) => logger(data)),
              map(({ response }) => {
                return {
                  type: QUEST_ACTION.ON_UPDATE_QUEST_SUCCESS,
                  payload: commonParser(response.data)
                }
              }),
              catchError((error) =>
                of({
                  type: QUEST_ACTION.ON_UPDATE_QUEST_FAILED,
                  payload: error
                })
              )
            )
        )
    )
  )

const handleGetQuestsEpic = (action$, state$) =>
  action$.pipe(
    ofType(QUEST_ACTION.ON_GET_QUESTS),
    map(() => ({
      brandId: extractBrandId(state$)
    })),
    filter(({ brandId }) => brandId),
    mergeMap(({ brandId }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .getBrandResources(API_PATHS.QUESTS, brandId, accessToken)
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => {
              return {
                type: QUEST_ACTION.ON_GET_QUESTS_SUCCESS,
                payload: commonParser(response.data) || []
              }
            }),
            catchError(() =>
              of({
                type: QUEST_ACTION.ON_GET_QUESTS_FAILED
              })
            )
          )
      )
    )
  )

export const questEpic = combineEpics(
  // Create Quest
  handleCreateQuestIfNoMediaChange,
  handleCreateQuestIfMediaChange,
  handleCreateQuestWithUploadMediaEpic,
  handleCreateQuestEpic,

  // Update Quest
  handleToggleQuestEpic,
  handleUpdateQuestIfNoMediaChange,
  handleUpdateQuestIfMediaChange,
  handleUpdateQuestWithUploadMediaEpic,
  handleUpdateQuestEpic,

  handleGetQuestsEpic
)
